import {useStage} from "@client/components/Stage";
import * as recipes from "@client/features/recipes";
import * as search from "@client/features/search";
import * as forms from "@client/forms";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import {server} from "@reactivated";
import * as Sentry from "@sentry/browser";
import React from "react";

const IMPORT_PLACEHOLDER = `1 cup white sugar
1/2 cup unsalted butter
2 large eggs
2 teaspoons vanilla extract
1.5 cups all-purpose flour
1.5 teaspoons baking powder
1/2 cup milk
`;

// Or https://www.allrecipes.com/recipe/17481/simple-white-cake/

export const CreateRecipe = router.CreateRecipe.withHooks(() => {
    const importForm = forms.useForm(server.journal.nutritional_ai.ImportRecipe);
    const importedRecipeForm = forms.useForm(server.journal.nutrition.CreateRecipe, {
        initial: {
            name: "",
            container_servings: null,
            container_quantity: null,
            serving_size_unit: "",
            container_unit: "",
            notes: "",
        },
    });

    const [route, setRoute] = React.useState<"import" | "confirm">("import");
    const stage = useStage<server.journal.nutrition.ExpandedEntry.output>({
        entries: [],
        target: null,
    });

    return {
        recipeForm: recipes.useRecipeForm({}),
        importForm,
        importedRecipeForm,
        stage,
        route,
        setRoute,
    };
})
    .withTab(
        {name: "index", title: "Manual", icon: "edit"},
        (props) => props.recipeForm.layout.component,
    )
    .withTab({name: "import", title: "Import", icon: "0758-download2"}, (props) => (
        <div className="space-y-2">
            {props.route == "import" ? (
                <div className="space-y-2">
                    <h2 className="text-xl">
                        Recipe importer<sup className="ml-1 text-indigo-800">beta</sup>
                    </h2>
                    <p>You can add instructions and portions later.</p>
                    <p>
                        This is an{" "}
                        <span className="font-medium text-indigo-800">
                            experimental
                        </span>{" "}
                        feature. Be patient.
                    </p>
                    <p>
                        If any ingredients look off, just skip them and manually fix
                        them afterwards
                    </p>
                    <h2 className="text-lg">List out your recipe ingredients</h2>
                    <forms.Widget
                        field={props.importForm.fields.description}
                        placeholder={IMPORT_PLACEHOLDER}
                    />
                </div>
            ) : (
                <div className="space-y-2">
                    <h2 className="text-lg">Name your recipe</h2>
                    <forms.Widget field={props.importedRecipeForm.fields.name} />
                    <h2 className="text-lg">And confirm recipe ingredients</h2>
                    {props.stage.entries}
                </div>
            )}
        </div>
    ))
    .withActions((props) => {
        if (props.currentTab === "import") {
            return props.route == "import"
                ? [
                      {
                          icon: "0758-download2",
                          title: "Import",
                          disabled: !props.importForm.validated.success,
                          onClick: async () => {
                              const response = await forms.submitForm(
                                  props.importForm,
                                  server.journal.nutritional_ai.import_recipe,
                              );
                              if (response == null) return;
                              const {stage} = props;

                              if (response.type == "success") {
                                  void analytics.track("ANALYZED_RECIPE");
                                  props.setRoute("confirm");

                                  stage.setEntries(response.data.entries);
                              } else {
                                  void analytics.track("ERROR_ANALYZING_RECIPE");
                                  props.notify(
                                      "Error processing recipe.\n\nTry again.\n\nOur team has been notified.",
                                      {type: "error", permanent: true},
                                  );
                                  Sentry.captureMessage("Error processing image");
                              }
                          },
                      },
                  ]
                : [
                      {
                          icon: "repeat",
                          title: "Start Over",
                          onClick: () => {
                              void analytics.track("RESTARTED_RECIPE_ANALYSIS");
                              props.setRoute("import");
                          },
                      },
                      {
                          icon: "save",
                          title: "Create",
                          disabled: !props.importedRecipeForm.validated.success,
                          onClick: async () => {
                              const response = await forms.submitForm(
                                  props.importedRecipeForm,
                                  server.journal.nutrition.create_recipe,
                              );
                              if (response == null) return;

                              const stageResult = props.stage.validate();

                              if (stageResult == null || response.type != "success") {
                                  throw new Error("Failed to create recipe");
                              }

                              const createdRecipe = recipes.addEntriesToRecipe({
                                  recipe: response.data,
                                  entries: stageResult.entries,
                              });

                              void router.push(
                                  "update_recipe.recipe",
                                  {
                                      recipeId: createdRecipe.id,
                                  },
                                  {update_recipe: createdRecipe},
                              );
                          },
                      },
                  ];
        }

        return [
            {
                icon: "save",
                title: "Save",
                onClick: async () => {
                    const response = await forms.submitForm(
                        props.recipeForm.form,
                        server.journal.nutrition.create_recipe,
                    );
                    if (response?.type === "success") {
                        void props.dispatch(search.loadSearch({reset: true}));
                        props.notify(
                            "Your recipe was created. Now add some ingredients.",
                        );
                        void router.push("update_recipe.recipe", {
                            recipeId: response.data.id,
                        });
                    }
                },
            },
        ];
    })
    .withOptions(() => ({
        title: "Create recipe",
        back: "Recipes",
    }));
